import { getPropertyCategory, type PropertyCategory } from './propertyTypeConfig';

/**
 * THE single source of truth for the property types offered in search UIs
 * (home-hero panel + /search sidebar). Ends the split where the hero rendered
 * lowercase ids from propertyFormOptions while the sidebar rendered whatever
 * UPPERCASE keys existed in the i18n dictionary (which leaked legacy aliases
 * like HOUSE/CONDO/COMMERCIAL as clickable "types" and silently dropped
 * TOWNHOUSE/SHOP that had no uppercase key).
 *
 * Membership = exactly the types the listing form can create
 * (propertyFormOptions RESIDENTIAL_TYPES + COMMERCIAL_TYPES), so search can
 * never offer a type that has no listings pathway.
 *
 * Labels are NOT stored here — resolve at render time from the existing i18n
 * dictionary: t(`search.filters.propertyTypeOptions.${option.enum}`).
 */
export interface SearchTypeOption {
  /** Lowercase id used by the listing form + hero URLs ('villa', 'rest_house'). */
  id: string;
  /** Canonical UPPERCASE backend enum ('VILLA', 'REST_HOUSE'). */
  enum: string;
  /**
   * The 2-way سكني/تجاري search toggle bucket. Preserves the established
   * product split (land/building/rest_house/chalet under residential; hotel/
   * farm under commercial) — intentionally NOT the same as `category`.
   */
  searchCategory: 'residential' | 'commercial';
  /** The 5-way config category (residential|land|commercial|hospitality|institutional). */
  category: PropertyCategory;
  /** Domain-icon registry id (all *_land variants share the 'land' glyph). */
  iconId: string;
}

const RESIDENTIAL_IDS = [
  'apartment', 'villa', 'floor', 'building', 'land', 'rest_house',
  'chalet', 'room', 'townhouse', 'studio', 'penthouse', 'duplex',
] as const;

const COMMERCIAL_IDS = [
  'office', 'commercial_building', 'warehouse', 'commercial_land',
  'industrial_land', 'shop', 'showroom', 'farm', 'agricultural_land',
  'mall', 'hotel', 'workshop', 'factory', 'school', 'health_center', 'station',
] as const;

const LAND_ICON_IDS = new Set(['land', 'commercial_land', 'industrial_land', 'agricultural_land']);

function toOption(id: string, searchCategory: 'residential' | 'commercial'): SearchTypeOption {
  const enumKey = id.toUpperCase();
  return {
    id,
    enum: enumKey,
    searchCategory,
    category: getPropertyCategory(enumKey),
    iconId: LAND_ICON_IDS.has(id) ? 'land' : id,
  };
}

export const SEARCH_TYPE_CATALOG: SearchTypeOption[] = [
  ...RESIDENTIAL_IDS.map((id) => toOption(id, 'residential')),
  ...COMMERCIAL_IDS.map((id) => toOption(id, 'commercial')),
];

/** Options for the active toggle; '' (all) returns the full catalog. */
export function getSearchTypeOptions(searchCategory: '' | 'residential' | 'commercial'): SearchTypeOption[] {
  if (!searchCategory) return SEARCH_TYPE_CATALOG;
  return SEARCH_TYPE_CATALOG.filter((o) => o.searchCategory === searchCategory);
}

/** Lookup by lowercase id OR uppercase enum (accepts either casing). */
export function findSearchType(idOrEnum: string): SearchTypeOption | undefined {
  if (!idOrEnum) return undefined;
  const lower = idOrEnum.toLowerCase();
  return SEARCH_TYPE_CATALOG.find((o) => o.id === lower);
}

/** Domain-icon registry id for any property type string (either casing). */
export function getPropertyTypeIconId(idOrEnum: string): string {
  return findSearchType(idOrEnum)?.iconId ?? 'building';
}
